import { Alert, Button, Checkbox, Form, Input, message, Modal, Row, Select, Skeleton } from "antd";
import { FormWizard } from "components/formWizard";
import { formatError, formatErrorMessage } from "common/redux/middleware/queryErrorLogger";
import { formatCurrency, parseCardExpirationDate } from "common/utils";
import { AktDatePicker } from "components/aktDatePicker";
import { useUserType } from "features/auth";
import { useFetchMeQuery } from "features/auth/authAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { useCreateCreditorPortalLoggingPaymentMethodMutation } from "features/payments/creditorPortal/paymentsAPI";
import {
  useCalculateConvenienceFeesMutation,
  useCreateAchPaymentMethodMutation,
  useCreateCardPaymentMethodMutation,
  useCreateLoggingPaymentMethodMutation,
} from "features/payments/paymentsAPI";
import { useFetchDebtorProfileQuery } from "features/accountsTable/agencyPortal/accountsTableAPI";
import { useFetchCreditorPortalDebtorProfileQuery } from "features/accountsTable/creditorPortal/accountsTableAPI";
import { useFetchDebtorAddressesQuery } from "features/debtorSider/agencyPortal/debtorSiderAPI";
import { selectPaymentsSlice } from "features/payments/paymentsSlice";
import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import currency from "currency.js";
import { PERMISSIONS, useAuthorization } from "features/permissions";
import { COUNTRIES, DATE_FORMAT } from "common/constants";
import { useFetchAgencyQuery } from "features/basicInfo/agencyPortal/agencySettingsAPI";

const StyledInput = styled(Input)`
  border-bottom: 1px solid grey;
  width: 200px;
  border-radius: 0px;
  &:hover,
  &:focus {
    border-bottom: 1px solid grey;
  }
`;

const StyledFormItem = styled(Form.Item)`
  margin-left: 4px;
  margin-right: 4px;
`;

const StyledDatePicker = styled(AktDatePicker)`
  &.ant-picker {
    border-radius: 0px;
    border-top: none;
    border-left: none;
    border-right: none;
    border-bottom: 1px solid grey;
  }
`;

const StyledFormItemContainer = styled(Form.Item)`
  margin-bottom: 0;
`;

const StyledSelect = styled(Select)`
  && {
    width: 200px;
  }
  border-bottom: 1px solid grey;
  & .ant-select-selector {
    margin-bottom: -2px;
  }
  &:hover,
  &:focus {
    border-bottom: 1px solid grey;
  }
`;

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 400px;
`;

function CreatePaymentMethodForm({ submitButtonText, confirmPaymentLoading, onOk, onCancel }) {
  const { isAgencyUserType, isCreditorUserType } = useUserType();
  const { debtorId } = useParams();
  const [currentConvenienceFee, setCurrentConvenienceFee] = useState(null);
  const [convenienceFeeError, setConvenienceFeeError] = useState(null);
  const [showDisclosureWizard, setShowDisclosureWizard] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingValues, setPendingValues] = useState(null);
  const { data: isCanWaiveConvenienceFeeAuthorized } = useAuthorization(
    PERMISSIONS.PAYMENT__WAIVE_CONVENIENCE_FEES,
  );

  const [form] = Form.useForm();
  const paymentsSlice = useSelector(selectPaymentsSlice);
  const [fetchConvenienceFees] = useCalculateConvenienceFeesMutation();
  const { data: agencyDebtorPersonalInfo, isLoading: isDebtorPersonalInfoLoading } =
    useFetchDebtorProfileQuery({ debtorId }, { skip: !isAgencyUserType });

  const { data: debtorAddresses, isLoading: isDebtorAddressLoading } = useFetchDebtorAddressesQuery(
    { debtorId },
    { skip: !isAgencyUserType },
  );
  const debtorAddress =
    debtorAddresses?.find((address) => address.isPrimary) ?? debtorAddresses?.[0];

  const { data: creditorPortalDebtorPersonalInfo } = useFetchCreditorPortalDebtorProfileQuery(
    { debtorId },
    { skip: isAgencyUserType },
  );
  const debtorPersonalInfo = isAgencyUserType
    ? agencyDebtorPersonalInfo
    : creditorPortalDebtorPersonalInfo;

  const [createAchPaymentMethod, { isLoading: isLoadingCreateAch }] =
    useCreateAchPaymentMethodMutation();
  const [createCardPaymentMethod, { isLoading: isLoadingCreateCard }] =
    useCreateCardPaymentMethodMutation();
  const [createAgencyLoggingPaymentMethod, { isLoading: isLoadingAgencyCreateLogging }] =
    useCreateLoggingPaymentMethodMutation();
  const [
    createCreditorPortalLoggingPaymentMethod,
    { isLoading: isLoadingCreditorPortalCreateLogging },
  ] = useCreateCreditorPortalLoggingPaymentMethodMutation();
  const [createLoggingPaymentMethod, isLoadingCreateLogging] = isAgencyUserType
    ? [createAgencyLoggingPaymentMethod, isLoadingAgencyCreateLogging]
    : [createCreditorPortalLoggingPaymentMethod, isLoadingCreditorPortalCreateLogging];

  const accountTypeOptions = [
    { value: "checking", label: "Checking" },
    { value: "savings", label: "Savings" },
  ];

  const { data: constants } = useFetchBackendConstantsQuery();

  const filterOption = (inputValue, option) => {
    const fullOptionText = option.label;
    return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  const { data: agencySettings } = useFetchAgencyQuery();
  const { data: me } = useFetchMeQuery();

  const isInternationalPaymentSupportEnabled = () =>
    agencySettings?.primaryPaymentProvider === "repay";
  const defaults = useMemo(() => {
    const paymentMethodOptions = [];
    let initialPaymentMethodOption = me?.enabledPaymentMethods?.defaultPaymentMethod;
    let processPaymentOptions = me?.enabledPaymentMethods?.enabledProcessedPaymentOptions || [];
    let loggingPaymentOptions = me?.enabledPaymentMethods?.enabledPaidToAgencyLoggedPaymentOptions;
    if (paymentsSlice.paidTo === "creditor") {
      loggingPaymentOptions = isCreditorUserType
        ? me?.enabledPaymentMethods?.enabledPaidToCreditorPortalPaymentOptions
        : me?.enabledPaymentMethods?.enabledPaidToCreditorPaymentOptions;
      processPaymentOptions = []; // These payments were already paid to the client/creditor, so we don't need to process them again.
      initialPaymentMethodOption = "paid_to_creditor";
    } else if (paymentsSlice.paidTo === "forwarding_entity") {
      loggingPaymentOptions =
        me?.enabledPaymentMethods?.enabledPaidToForwardingEntityPaymentOptions;
      processPaymentOptions = []; // These payments were already paid to a forwarding entity, so we don't need to process them again.
      initialPaymentMethodOption = "forwarding_entity";
    } else if (paymentsSlice.isPaymentPlan) {
      loggingPaymentOptions = me?.enabledPaymentMethods?.enabledPaidToAgencyLoggedPaymentOptions;
    } else if (moment(paymentsSlice.paymentsSchedule[0].scheduledDate).unix() > moment().unix()) {
      loggingPaymentOptions = me?.enabledPaymentMethods?.enabledPaidToAgencyLoggedPaymentOptions;
    } else {
      // NOTE: This case should only happen when the payment is scheduled for today.
      // Right now they are unable to schedule payments in the past (see shouldDisableDate). This was intentionally
      // asked by CBY. We may choose to add permissioning around this in the future, so leaving this here.
      loggingPaymentOptions = me?.enabledPaymentMethods?.enabledPaidToAgencyLoggedPaymentOptions;
    }

    // Categorize payment methods into process and logging options.
    // e.g
    // paymentMethodOptions = [
    //   {
    //     label: "Process options",
    //     options: [
    //       { value: "process_card", label: "Card" },
    //       { value: "process_ach", label: "ACH" },
    //     ],
    //   },
    //   {
    //     label: "Logging options",
    //     options: [
    //       { value: "card", label: "Card" },
    //       { value: "ach", label: "ACH" },
    //       { value: "check", label: "Check" },
    //       { value: "cash", label: "Cash" },
    //       { value: "bankruptcy", label: "Bankruptcy Payment" },
    //       { value: "online_portal", label: "Online Portal" },
    //       { value: "legal", label: "Legal" },
    //       { value: "other", label: "Other" },
    //     ],
    //   },
    // ];
    if (processPaymentOptions.length > 0) {
      paymentMethodOptions.push({
        label: "Process options",
        options: processPaymentOptions,
      });
    }
    if (loggingPaymentOptions.length > 0) {
      paymentMethodOptions.push({
        label: "Logging options",
        options: loggingPaymentOptions,
      });
    }

    return { paymentMethodOptions, initialPaymentMethodOption };
  }, [
    paymentsSlice.paidTo,
    paymentsSlice.isPaymentPlan,
    paymentsSlice.paymentsSchedule,
    isCreditorUserType,
    me?.enabledPaymentMethods,
  ]);

  const initialValues = {
    paymentMethodType: defaults.initialPaymentMethodOption,
    firstName: debtorPersonalInfo?.firstName,
    lastName: debtorPersonalInfo?.lastName,
    address1: debtorAddress?.address1,
    address2: debtorAddress?.address2,
    city: debtorAddress?.city,
    state: debtorAddress?.state,
    zipCode: debtorAddress?.zipCode,
    emails: debtorPersonalInfo?.email,
    waiveConvenienceFees: false,
  };

  // NOTE: handleConvenienceFee is wrapped with a useCallback because it's used in the useEffect and onPaymentMethodChange,
  // and the dependencies change on every render. The useCallback ensures that the function is only recreated when the dependencies change.
  const handleConvenienceFee = useCallback(async () => {
    const paymentMethodType = form.getFieldValue("paymentMethodType");
    const isCardFeeEnabled =
      paymentMethodType === "process_card" && agencySettings?.isConvenienceFeeCardPaymentsEnabled;
    const isAchFeeEnabled =
      paymentMethodType === "process_ach" && agencySettings?.isConvenienceFeeAchPaymentsEnabled;

    if (isCardFeeEnabled || isAchFeeEnabled) {
      // Convenience fees are only applicable to process card and process ach payments.
      const result = await fetchConvenienceFees({
        accountIds: paymentsSlice.selectedAccounts,
        amounts: [paymentsSlice.paymentsSchedule[0]?.totalAmount],
        state: form.getFieldValue("state"),
        zipCode: form.getFieldValue("zipCode"),
        paymentMethodType: form.getFieldValue("paymentMethodType"),
      });
      if ("data" in result) {
        setCurrentConvenienceFee(result.data.convenienceFees[0]);
        setConvenienceFeeError(null);
      } else {
        setConvenienceFeeError(formatErrorMessage(result.error));
      }
    } else {
      setCurrentConvenienceFee({ message: null, fee: 0, amount: 0 });
      setConvenienceFeeError(null);
    }
  }, [
    fetchConvenienceFees,
    paymentsSlice.selectedAccounts,
    paymentsSlice.paymentsSchedule,
    form,
    agencySettings?.isConvenienceFeeCardPaymentsEnabled,
    agencySettings?.isConvenienceFeeAchPaymentsEnabled,
  ]);

  useEffect(() => {
    (async () => {
      await handleConvenienceFee();
    })();
  }, [handleConvenienceFee]);

  const onBillingStateChange = async () => {
    await handleConvenienceFee();
  };

  const onCountryChange = async () => {
    form.setFieldsValue({ state: undefined, zipCode: undefined });
    await handleConvenienceFee();
  };

  const onPaymentMethodChange = async () => {
    await handleConvenienceFee();
  };

  const handleDisclosureWizardDone = async () => {
    if (!pendingAction) return;

    const result = await pendingAction();
    if ("data" in result) {
      setShowDisclosureWizard(false);
      setPendingAction(null);
      setPendingValues(null);
      return onOk(
        result.data.id,
        pendingValues.referenceNumber,
        pendingValues.promiseToPayDate,
        pendingValues.promiseToPayLastDate,
        pendingValues.waiveConvenienceFees,
        pendingValues.notes,
        pendingValues.attributionSource,
      );
    }

    if ("error" in result) {
      form.setFields(formatError(result.error));
      message.error("Failed to process the payment. Please refresh and try again.");
      setShowDisclosureWizard(false);
      setPendingAction(null);
      setPendingValues(null);
    }
  };

  const handleDisclosureWizardCancel = () => {
    setShowDisclosureWizard(false);
    setPendingAction(null);
    setPendingValues(null);
  };

  const disclosureSteps = useMemo(() => {
    const steps = [];
    const waiveConvenienceFees = form.getFieldValue("waiveConvenienceFees");

    if (currency(currentConvenienceFee?.fee).value > 0 && !waiveConvenienceFees) {
      steps.push({
        title: "Convenience Fee Disclosure",
        doneButtonText: "Pay",
        form: (
          <div>
            <p>
              Attention:{" "}
              {currentConvenienceFee?.message ||
                "You are authorizing the payment service provider to charge your payment method for the principal amount and a convenience fee. For ACH payments, this will appear on your statement as two separate transactions. For card payments, this will appear as one transaction. You understand that you are paying a fee for the convenience of making this payment by phone and that you can avoid this fee by mailing a check or money order to our office."}
            </p>
            <p>
              <strong>Convenience Fee: {formatCurrency(currentConvenienceFee?.fee)}</strong>
            </p>
            <Form.Item
              name="convenienceFeeAcknowledged"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error("You must acknowledge the convenience fee disclosure"),
                        ),
                },
              ]}
            >
              <Checkbox>I understand and agree to the convenience fee disclosure above.</Checkbox>
            </Form.Item>
          </div>
        ),
      });
    }

    if (agencySettings?.paymentDisclosureNotice) {
      steps.push({
        title: "Payment Disclosure",
        doneButtonText: "Pay",
        form: (
          <div>
            <p>{agencySettings?.paymentDisclosureNotice}</p>
            <Form.Item
              name="paymentAcknowledged"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(new Error("You must acknowledge the payment disclosure")),
                },
              ]}
            >
              <Checkbox>I understand and agree to the payment disclosure above.</Checkbox>
            </Form.Item>
          </div>
        ),
      });
    }

    return steps;
  }, [
    currentConvenienceFee,
    form,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    form.getFieldValue("waiveConvenienceFees"),
    agencySettings?.paymentDisclosureNotice,
  ]);

  const onCreateNewPaymentMethodFinish = async (values) => {
    const {
      waiveConvenienceFees,
      paymentMethodType,
      emails,
      referenceNumber,
      attributionSource,
      promiseToPayDate,
      promiseToPayLastDate,
      notes,
    } = values;
    const emailsArray = emails ? emails.split(",").map((email) => email.trim()) : [];

    const action = {
      process_card: () => {
        const { expirationDate } = values;
        const [cardExpMonth, cardExpYear] = parseCardExpirationDate(expirationDate);
        return createCardPaymentMethod({
          ...values,
          applyConvenienceFees: !waiveConvenienceFees,
          debtorId,
          cardExpMonth,
          cardExpYear,
          emails: emailsArray,
          // TODO make this configurable via agency settings
          paymentProvider: "repay",
        });
      },
      process_ach: () =>
        createAchPaymentMethod({
          ...values,
          applyConvenienceFees: !waiveConvenienceFees,
          debtorId,
          emails: emailsArray,
          // TODO make this configurable via agency settings
          paymentProvider: "repay",
        }),
      card: () =>
        createLoggingPaymentMethod({
          paymentMethodType,
          debtorId,
        }),
      ach: () =>
        createLoggingPaymentMethod({
          ...values,
          paymentMethodType,
          debtorId,
          emails: emailsArray,
        }),
      balance_transfer: () =>
        createLoggingPaymentMethod({
          ...values,
          paymentMethodType,
          debtorId,
          emails: emailsArray,
        }),
      check: () =>
        createLoggingPaymentMethod({
          paymentMethodType,
          debtorId,
        }),
      cash: () =>
        createLoggingPaymentMethod({
          paymentMethodType,
          debtorId,
        }),
      other: () =>
        createLoggingPaymentMethod({
          paymentMethodType,
          debtorId,
        }),
      online_portal: () =>
        createLoggingPaymentMethod({
          paymentMethodType,
          debtorId,
        }),
      paid_to_creditor: () =>
        createLoggingPaymentMethod({
          paymentMethodType,
          debtorId,
        }),
      paid_to_creditor_legal: () =>
        createLoggingPaymentMethod({
          paymentMethodType,
          debtorId,
        }),
      // HACK: For now, we're just going to always create cash payment methods
      // Later, we will refactor this to either use the correct endpoint,
      // or retrieve the dynamic list of logging payment method options.
      forwarding_entity: () =>
        createLoggingPaymentMethod({
          paymentMethodType,
          debtorId,
        }),
      forwarding_entity_legal: () =>
        createLoggingPaymentMethod({
          paymentMethodType,
          debtorId,
        }),
      cashiers_check: () =>
        createLoggingPaymentMethod({
          paymentMethodType,
          debtorId,
        }),
      money_order: () =>
        createLoggingPaymentMethod({
          paymentMethodType,
          debtorId,
        }),
      third_party: () =>
        createLoggingPaymentMethod({
          paymentMethodType,
          debtorId,
        }),
      bankruptcy: () =>
        createLoggingPaymentMethod({
          paymentMethodType,
          debtorId,
        }),
      legal: () =>
        createLoggingPaymentMethod({
          paymentMethodType,
          debtorId,
        }),
      promise_to_pay: () =>
        createLoggingPaymentMethod({
          paymentMethodType,
          debtorId,
        }),
    }[paymentMethodType];

    const isProcessPaymentMethod =
      paymentMethodType === "process_ach" || paymentMethodType === "process_card";
    const shouldShowDisclosure =
      (currency(currentConvenienceFee?.fee).value > 0 && !values.waiveConvenienceFees) ||
      (isProcessPaymentMethod && agencySettings?.paymentDisclosureNotice);

    if (shouldShowDisclosure) {
      setPendingAction(() => action);
      setPendingValues({
        referenceNumber,
        promiseToPayDate,
        promiseToPayLastDate,
        waiveConvenienceFees,
        notes,
        attributionSource,
      });
      setShowDisclosureWizard(true);
    } else {
      const result = await action();
      if ("data" in result) {
        return onOk(
          result.data.id,
          referenceNumber,
          promiseToPayDate,
          promiseToPayLastDate,
          waiveConvenienceFees,
          notes,
          attributionSource,
        );
      }
      if ("error" in result) {
        form.setFields(formatError(result.error));
        message.error("Failed to process the payment. Please refresh and try again.");
      }
    }
  };

  const billingAddressFormFields = (required) => (
    <>
      <h3>Billing Details</h3>
      <StyledFormItemContainer>
        <Row>
          <StyledFormItem label="First Name" name="firstName" rules={[{ required }]}>
            <StyledInput bordered={false} maxLength={100} placeholder="First Name" />
          </StyledFormItem>
          <StyledFormItem label="Last Name" name="lastName" rules={[{ required }]}>
            <StyledInput bordered={false} maxLength={100} placeholder="Last Name" />
          </StyledFormItem>
        </Row>
      </StyledFormItemContainer>
      <StyledFormItemContainer>
        <Row>
          <StyledFormItem label="Address 1" name="address1" rules={[{ required }]}>
            <StyledInput bordered={false} maxLength={100} placeholder="Address 1" />
          </StyledFormItem>
          <StyledFormItem label="Address 2" name="address2">
            <StyledInput bordered={false} maxLength={100} placeholder="Address 2" />
          </StyledFormItem>
        </Row>
      </StyledFormItemContainer>
      {isInternationalPaymentSupportEnabled() ? (
        <>
          <StyledFormItemContainer>
            <Row>
              <StyledFormItem
                label="Country"
                name="country"
                initialValue={debtorAddress?.country ?? "US"}
                rules={[{ required }]}
              >
                <StyledSelect
                  popupMatchSelectWidth={false}
                  bordered={false}
                  placeholder="Country"
                  options={COUNTRIES}
                  filterOption={filterOption}
                  onChange={onCountryChange}
                  showSearch
                />
              </StyledFormItem>
            </Row>
          </StyledFormItemContainer>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.country !== currentValues.country
            }
          >
            {({ getFieldValue }) => {
              const country = getFieldValue("country");
              const isUs = country === "US";
              const isCa = country === "CA";
              const provinceList = isCa ? constants?.canadianProvinces : constants?.states;
              const zipRules = [{ required }];
              let zipMaxLength = 20;
              let zipPlaceholder = "Zip Code/Postal Code";
              if (isUs) {
                zipRules.push({
                  pattern: /^\d{5}(-\d{4})?$/,
                  message: "Must be a valid zip code",
                });
                zipMaxLength = 5;
                zipPlaceholder = "12345";
              } else if (isCa) {
                zipRules.push({
                  pattern: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
                  message: "Must be a valid Canadian postal code",
                });
                zipMaxLength = 7;
                zipPlaceholder = "A1A 1A1";
              }
              return (
                <StyledFormItemContainer>
                  <Row wrap={false}>
                    <StyledFormItem label="City" name="city" rules={[{ required }]}>
                      <StyledInput bordered={false} maxLength={100} placeholder="City" />
                    </StyledFormItem>
                    <StyledFormItem label="State/Province" name="state" rules={[{ required }]}>
                      {isUs || isCa ? (
                        <StyledSelect
                          popupMatchSelectWidth={false}
                          bordered={false}
                          placeholder="State/Province"
                          options={provinceList?.map(({ display, value }) => ({
                            value,
                            label: display,
                          }))}
                          filterOption={filterOption}
                          onChange={onBillingStateChange}
                          showSearch
                        />
                      ) : (
                        <StyledInput
                          bordered={false}
                          maxLength={100}
                          placeholder="State/Province"
                          onBlur={onBillingStateChange}
                        />
                      )}
                    </StyledFormItem>
                    <StyledFormItem name="zipCode" label="Zip Code/Postal Code" rules={zipRules}>
                      <StyledInput
                        bordered={false}
                        maxLength={zipMaxLength}
                        placeholder={zipPlaceholder}
                      />
                    </StyledFormItem>
                  </Row>
                </StyledFormItemContainer>
              );
            }}
          </Form.Item>
        </>
      ) : (
        <StyledFormItemContainer>
          <Row wrap={false}>
            <StyledFormItem label="City" name="city" rules={[{ required }]}>
              <StyledInput bordered={false} maxLength={100} placeholder="City" />
            </StyledFormItem>
            <StyledFormItem label="State" name="state" rules={[{ required }]}>
              <StyledSelect
                popupMatchSelectWidth={false}
                bordered={false}
                placeholder="State"
                options={constants?.states.map(({ display, value }) => ({
                  value,
                  label: display,
                }))}
                filterOption={filterOption}
                onChange={onBillingStateChange}
                showSearch
              />
            </StyledFormItem>
            <StyledFormItem
              name="zipCode"
              label="Zip Code"
              rules={[
                { required },
                { pattern: /^\d{5}(-\d{4})?$/, message: "Must be a valid zip code" },
              ]}
            >
              <StyledInput bordered={false} maxLength={5} placeholder="Zip Code" />
            </StyledFormItem>
          </Row>
        </StyledFormItemContainer>
      )}
      <StyledFormItemContainer>
        <StyledFormItem label="Email(s)" name="emails">
          <StyledInput
            bordered={false}
            style={{ width: 250 }}
            maxLength={100}
            placeholder="Emails (comma separated)"
          />
        </StyledFormItem>
      </StyledFormItemContainer>
    </>
  );

  const getButtonText = (applyConvenienceFees) => {
    // For one-time payments
    if (!paymentsSlice.isPaymentPlan) {
      const amount = currency(paymentsSlice.totalAmount);
      if (applyConvenienceFees) {
        return `Confirm Payment (${formatCurrency(amount.add(currentConvenienceFee?.fee))})`;
      }
      return `Confirm Payment (${formatCurrency(amount)})`;
    }

    // For payment planss with down payments
    if (paymentsSlice.downPaymentAmount > 0) {
      // if there's a fee, we need to add it to the down payment
      const totalAmount = currency(paymentsSlice.downPaymentAmount);
      if (applyConvenienceFees) {
        return `Confirm Payment (${formatCurrency(
          totalAmount.add(currency(currentConvenienceFee?.fee)),
        )})`;
      }

      return `Confirm Payment (${formatCurrency(totalAmount)})`;
    }

    // For payment plans without a down payment (no payment today), it doesn't matter to show if there's a fee or not
    // unless there's a payment today.
    if (paymentsSlice.paymentsSchedule[0].scheduledDate === moment().format(DATE_FORMAT)) {
      const totalAmount = currency(paymentsSlice.paymentsSchedule[0].totalAmount);
      if (applyConvenienceFees) {
        return `Confirm Payment (${formatCurrency(
          currency(totalAmount).add(currency(currentConvenienceFee?.fee)),
        )})`;
      }
      return `Confirm Payment (${formatCurrency(totalAmount)})`;
    }

    return "Setup Payment Plan";
  };

  return isDebtorPersonalInfoLoading || isDebtorAddressLoading ? (
    <Skeleton active />
  ) : (
    <>
      <StyledForm
        form={form}
        layout="vertical"
        validateMessages={{ required: "This is a required field" }}
        initialValues={initialValues}
        onFinish={onCreateNewPaymentMethodFinish}
      >
        <Form.Item name="paymentMethodType" label="New Payment Method:">
          <Select onChange={onPaymentMethodChange} options={defaults.paymentMethodOptions} />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.paymentMethodType !== currentValues.paymentMethodType
          }
        >
          {({ getFieldValue }) => {
            const paymentMethodType = getFieldValue("paymentMethodType");
            const paymentInfoRequired = ["process_card", "process_ach"].includes(paymentMethodType);

            switch (paymentMethodType) {
              case "process_card":
                return (
                  <>
                    <h3>Card Information</h3>
                    <StyledFormItem
                      name="cardNumber"
                      label="Card Number"
                      rules={[
                        { required: true },
                        {
                          pattern: /^[0-9]+$/,
                          message: "Please enter a valid number.",
                        },
                      ]}
                    >
                      <StyledInput bordered={false} maxLength={16} placeholder="Card Number" />
                    </StyledFormItem>
                    <StyledFormItem rules={[{ required: true }]}>
                      <Input.Group compact>
                        <Form.Item
                          label="Expiration Date"
                          name="expirationDate"
                          rules={[{ required: true }]}
                        >
                          <StyledDatePicker placeholder="MM/YY" format="MM/YY" />
                        </Form.Item>
                        <StyledFormItem
                          label="CVV"
                          name="cvv"
                          rules={[
                            {
                              pattern: /^[0-9]{3,4}$/,
                              message: "Please enter a valid CVV.",
                            },
                          ]}
                        >
                          <StyledInput bordered={false} placeholder="CVV" maxLength={4} />
                        </StyledFormItem>
                      </Input.Group>
                    </StyledFormItem>
                    {billingAddressFormFields(paymentInfoRequired)}
                  </>
                );
              case "process_ach":
              case "ach":
              case "balance_transfer":
                return (
                  <>
                    <Form.Item
                      label="Bank Account Name"
                      name="bankAccountName"
                      rules={[
                        { required: paymentInfoRequired },
                        {
                          type: "string",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="bankRoutingNumber"
                      label="Routing Number"
                      rules={[
                        { required: paymentInfoRequired },
                        {
                          pattern: /^[0-9]+$/,
                          message: "Please enter a valid routing number.",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter routing number..."
                        style={{ width: 200 }}
                        maxLength={9}
                      />
                    </Form.Item>
                    <Form.Item
                      name="bankAccountNumber"
                      label="Account Number"
                      rules={[
                        { required: paymentInfoRequired },
                        {
                          pattern: /^[0-9]+$/,
                          message: "Please enter a valid account number.",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter account number..."
                        style={{ width: 200 }}
                        maxLength={17}
                      />
                    </Form.Item>
                    <Form.Item name="bankAccountType" label="Account Type" initialValue="checking">
                      <Select options={accountTypeOptions} />
                    </Form.Item>
                    <Form.Item label="Description" name="description">
                      <Input />
                    </Form.Item>
                    {billingAddressFormFields(paymentInfoRequired)}
                  </>
                );
              case "check":
              case "money_order":
              case "cashiers_check":
                return (
                  <Form.Item
                    label="Reference Number"
                    name="referenceNumber"
                    rules={[
                      { required: false },
                      {
                        type: "string",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                );
              case "online_portal":
                return (
                  <Form.Item name="attributionSource" label="Payment Attribution">
                    <Select
                      allowClear
                      popupMatchSelectWidth={false}
                      options={constants?.paymentAttributionSources?.map((each) => ({
                        label: each.display,
                        value: each.value,
                      }))}
                    />
                  </Form.Item>
                );
              default:
                return null;
            }
          }}
        </Form.Item>

        <Form.Item name="notes" label="Payment Notes">
          <Input.TextArea rows={4} />
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.paymentMethodType !== currentValues.paymentMethodType
          }
        >
          {({ getFieldValue }) => {
            const isProcessPayment =
              getFieldValue("paymentMethodType") === "process_ach" ||
              getFieldValue("paymentMethodType") === "process_card";
            return (
              <>
                {/* Show the convenience fee waiver checkbox if process payment method */}
                {isProcessPayment &&
                  (isCanWaiveConvenienceFeeAuthorized ? (
                    <Form.Item valuePropName="checked" name="waiveConvenienceFees" label="">
                      <Checkbox>
                        Waive convenience fee of {formatCurrency(currentConvenienceFee?.fee)}
                      </Checkbox>
                    </Form.Item>
                  ) : (
                    <Form.Item>
                      <i>
                        A convenience fee of {formatCurrency(currentConvenienceFee?.fee)} will be
                        applied.
                      </i>
                    </Form.Item>
                  ))}
              </>
            );
          }}
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.waiveConvenienceFees !== currentValues.waiveConvenienceFees ||
            prevValues.paymentMethodType !== currentValues.paymentMethodType
          }
        >
          {({ getFieldValue }) => {
            const waiveConvenienceFees = getFieldValue("waiveConvenienceFees");
            const applyConvenienceFees = !waiveConvenienceFees;
            const isProcessPayment =
              getFieldValue("paymentMethodType") === "process_ach" ||
              getFieldValue("paymentMethodType") === "process_card";
            const isFeeNotReady =
              isProcessPayment &&
              !waiveConvenienceFees &&
              (!!convenienceFeeError || currentConvenienceFee === null);

            return (
              <Form.Item>
                {isProcessPayment && !!convenienceFeeError && (
                  <Alert
                    type="error"
                    showIcon
                    message={`Can't calculate fee. ${convenienceFeeError}`}
                    style={{ marginBottom: 8 }}
                  />
                )}
                <Button onClick={onCancel}>Back</Button>
                <Button
                  loading={
                    isLoadingCreateAch ||
                    isLoadingCreateCard ||
                    isLoadingCreateLogging ||
                    confirmPaymentLoading
                  }
                  disabled={isFeeNotReady}
                  style={{ marginLeft: 8 }}
                  type="primary"
                  htmlType="submit"
                >
                  {getButtonText(applyConvenienceFees)}
                </Button>
              </Form.Item>
            );
          }}
        </Form.Item>
      </StyledForm>
      {showDisclosureWizard && disclosureSteps?.length > 0 && (
        <Modal
          maskClosable={false}
          title="Acknowledgement and Agreement of Disclosures"
          open={showDisclosureWizard}
          onCancel={handleDisclosureWizardCancel}
          footer={null}
          width={700}
          closable
        >
          <FormWizard
            steps={disclosureSteps}
            onCancel={handleDisclosureWizardCancel}
            onDone={handleDisclosureWizardDone}
            initialValues={{
              convenienceFeeAcknowledged: false,
              paymentAcknowledged: false,
            }}
          />
        </Modal>
      )}
    </>
  );
}

export default CreatePaymentMethodForm;
